import { Button } from "@/components/ui/button";
import { Share2, Facebook, Send, Copy, Instagram, Linkedin } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

interface SocialShareProps {
  title: string;
  description?: string;
  url?: string;
  image?: string;
  hashtags?: string[];
  price?: number;
  courseData?: {
    faculty?: string;
    duration?: number;
    format?: string[];
    status?: string;
  };
  course?: {
    name?: string;
    image_url?: string;
    faculties?: {
      name?: string;
    };
  };
}

export const SocialShare = ({ 
  title, 
  description = "", 
  url, 
  image, 
  hashtags = [],
  price,
  courseData,
  course 
}: SocialShareProps) => {
  const currentUrl = url || window.location.href;
  
  // Ensure image URL is absolute
  const getAbsoluteImageUrl = (imageUrl: string | undefined) => {
    if (!imageUrl) return undefined;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    return `https://globalitacademy.am${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  };
  
  // Get share image - prioritize passed image prop first
  const getShareImage = () => {
    // Priority 1: Use passed image prop (for news, events, etc.)
    if (image) {
      return image;
    }
    
    // Priority 2: Use course-specific logic
    if (course) {
      // Use course's own image if available
      if (course.image_url) {
        return course.image_url.startsWith('http') 
          ? course.image_url 
          : `https://globalitacademy.am${course.image_url}`;
      }
      
      // Faculty-specific fallback images
      const courseName = course.name?.toLowerCase() || '';
      const facultyName = course.faculties?.name?.toLowerCase() || '';
      
      // Backend courses
      if (courseName.includes('backend') || courseName.includes('back-end') || 
          courseName.includes('back end') || facultyName.includes('backend')) {
        return 'https://globalitacademy.am/lovable-uploads/9610763a-5722-43d0-9058-e6e750bcd507.png';
      }
      
      // Frontend/Web courses
      if (courseName.includes('frontend') || courseName.includes('front-end') || 
          courseName.includes('front end') || courseName.includes('վեբ') || 
          courseName.includes('web') || facultyName.includes('frontend') || 
          facultyName.includes('վեբ') || facultyName.includes('web')) {
        return 'https://globalitacademy.am/lovable-uploads/3aef1e6b-6e35-4249-905f-4d58c0135648.png';
      }
      
      // Cybersecurity courses
      if (courseName.includes('կիբեռ') || courseName.includes('cyber') || 
          courseName.includes('անվտանգություն') || courseName.includes('security') ||
          facultyName.includes('կիբեռ') || facultyName.includes('cyber')) {
        return 'https://globalitacademy.am/lovable-uploads/1d875431-108a-485d-a4e3-e27286786183.png';
      }
      
      // Mobile/Web Programming courses
      if (courseName.includes('վեբ ծրագրավորում') || courseName.includes('web programming') ||
          courseName.includes('mobile') || courseName.includes('մոբայլ')) {
        return 'https://globalitacademy.am/lovable-uploads/a3874ffc-061b-4601-a181-737a30e41c4a.png';
      }
    }
    
    // Priority 3: Default fallback image
    return 'https://globalitacademy.am/lovable-uploads/9610763a-5722-43d0-9058-e6e750bcd507.png';
  };
  
  // Enhanced share text with course details
  const getEnhancedShareText = (platform: string) => {
    let baseText = `${title}`;
    
    if (description) {
      baseText += ` - ${description}`;
    }
    
    // Add course-specific information
    if (courseData) {
      if (courseData.faculty) baseText += ` | ${courseData.faculty}`;
      if (courseData.duration) baseText += ` | ${courseData.duration} ամիս`;
      if (price) baseText += ` | ${price.toLocaleString()} դր․/ամիս`;
      if (courseData.status === 'discount') baseText += ` | Զեղչով`;
      if (courseData.status === 'new') baseText += ` | Նոր դասընթաց`;
    }
    
    baseText += ` | Global IT Academy - Երևան, Հայաստան`;
    
    // Add hashtags for specific platforms
    if (platform === 'instagram' || platform === 'linkedin') {
      const courseHashtags = [
        '#GlobalITAcademy',
        '#ծրագրավորում',
        '#ITուսուցում',
        '#Երևան',
        '#Հայաստան',
        ...hashtags
      ];
      
      if (courseData?.faculty) {
        if (courseData.faculty.includes('Վեբ')) courseHashtags.push('#WebDevelopment');
        if (courseData.faculty.includes('Կիբեռ')) courseHashtags.push('#Cybersecurity');
        if (courseData.faculty.includes('Ծրագրավորման')) courseHashtags.push('#Programming');
      }
      
      baseText += `\n\n${courseHashtags.join(' ')}`;
    }
    
    return baseText;
  };
  

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentUrl);
    toast.success("Հղումը պատճենվեց");
  };

  const handleFacebookShare = () => {
    const shareText = getEnhancedShareText('facebook');
    const shareImage = getAbsoluteImageUrl(getShareImage());
    let facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}&quote=${encodeURIComponent(shareText)}`;
    
    // Add image if available
    if (shareImage) {
      facebookUrl += `&picture=${encodeURIComponent(shareImage)}`;
    }
    
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const handleInstagramShare = () => {
    const shareText = getEnhancedShareText('instagram');
    // Instagram doesn't have direct URL sharing, so copy text for user to paste
    navigator.clipboard.writeText(`${shareText}\n\n${currentUrl}`);
    toast.success("Տեքստը պատճենվեց Instagram-ի համար", {
      description: "Բացեք Instagram և փակցրեք պատճենված տեքստը"
    });
  };

  const handleLinkedInShare = () => {
    const shareText = getEnhancedShareText('linkedin');
    const shareImage = getAbsoluteImageUrl(getShareImage());
    let linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(shareText)}`;
    
    // Add image source if available
    if (shareImage) {
      linkedinUrl += `&source=${encodeURIComponent(shareImage)}`;
    }
    
    window.open(linkedinUrl, '_blank', 'width=600,height=400');
  };

  const handleTelegramShare = () => {
    const shareText = getEnhancedShareText('telegram');
    const shareImage = getAbsoluteImageUrl(getShareImage());
    
    // Telegram will automatically fetch OpenGraph image from the URL
    // But we can include image URL in text as fallback
    let telegramText = shareText;
    if (shareImage) {
      telegramText += `\n\n📷 ${shareImage}`;
    }
    
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(telegramText)}`;
    window.open(telegramUrl, '_blank', 'width=600,height=400');
  };

  const handleWhatsAppShare = () => {
    const shareText = getEnhancedShareText('whatsapp');
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${currentUrl}`)}`;
    window.open(whatsappUrl, '_blank', 'width=600,height=400');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: getEnhancedShareText('native'),
          url: currentUrl,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="min-w-[120px]">
          <Share2 className="h-4 w-4 mr-2" />
          Կիսվել
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {navigator.share && (
          <>
            <DropdownMenuItem onClick={handleNativeShare}>
              <Share2 className="h-4 w-4 mr-2" />
              Կիսվել (Սիստեմ)
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        
        <DropdownMenuItem onClick={handleFacebookShare}>
          <Facebook className="h-4 w-4 mr-2" />
          Facebook
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={handleInstagramShare}>
          <Instagram className="h-4 w-4 mr-2" />
          Instagram
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={handleLinkedInShare}>
          <Linkedin className="h-4 w-4 mr-2" />
          LinkedIn
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem onClick={handleTelegramShare}>
          <Send className="h-4 w-4 mr-2" />
          Telegram
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={handleWhatsAppShare}>
          <div className="h-4 w-4 mr-2">📱</div>
          WhatsApp
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem onClick={handleCopyLink}>
          <Copy className="h-4 w-4 mr-2" />
          Պատճենել հղումը
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};